import IconButton from "@/components/common/GoogleIconButton";
import TextField from "@/components/common/GoogleTextField";
import { useSpeechRecognition } from "@/hooks/useSpeechRecognition";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";
import { useBusinessUnitStore } from "@/stores/businessUnit";
import type { FileAttachment } from "@/types";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import {
  AlertCircle,
  FileText,
  Image,
  Loader2,
  Mic,
  Paperclip,
  Send,
  Square,
  X,
} from "lucide-react";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useEffectiveFacility } from "../hooks/useEffectiveFacility";
import {
  detectFormReference,
  detectScreenReference,
} from "../hooks/useScreenContextDetection";
import type { PageContext } from "../stores/chat";
import { useChatStore } from "../stores/chat";
import { ChatContextConsent } from "./ChatContextConsent";
import { ChatFollowups } from "./ChatFollowups";
import { ChatPendingFiles } from "./ChatPendingFiles";
import { ContextSharingControls } from "./ContextSharingControls";
import { useChatAttachments } from "./useChatAttachments";

const PLACEHOLDER_HINTS = [
  "Ask about emissions or ESG...",
  "Ask about carbon accounting...",
  "Type / to select a business unit",
  "Ask about report generation...",
  "Ask about Scope 1-3...",
];

interface ChatInputProps {
  /** Return false synchronously when consent is needed; retain the whole draft. */
  onSend: (
    message: string,
    files?: FileAttachment[],
  ) => void | false | Promise<void>;
  disabled: boolean;
  showPlaceholderHint?: boolean;
  followUpQuestions?: string[] | null;
  onSendFollowup?: (question: string) => void;
  onStop?: () => void;
  pageContextOverride?: PageContext | null;
  getPageContext?: () => PageContext;
  screenContextPrompt?: { message: string } | null;
  onScreenContextPromptHandled?: () => void;
  compact?: boolean;
}

export const ChatInput = memo(function ChatInput({
  onSend,
  disabled,
  showPlaceholderHint,
  followUpQuestions,
  onSendFollowup,
  onStop,
  pageContextOverride,
  getPageContext,
  screenContextPrompt,
  onScreenContextPromptHandled,
  compact = false,
}: ChatInputProps) {
  const { key: contextKey } = useLocation();
  const [input, setInput] = useState("");

  const [showSlashMenu, setShowSlashMenu] = useState(false);
  const [slashMenuItems, setSlashMenuItems] = useState<
    { id: string; label: string; type: "bu" | "action" }[]
  >([]);
  const [selectedSlashIndex, setSelectedSlashIndex] = useState(0);
  const [hintIndex, setHintIndex] = useState(0);

  const [attachAnchor, setAttachAnchor] = useState<HTMLElement | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const slashMenuRef = useRef<HTMLDivElement>(null);
  const screenDetectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const token = useAuthStore((s) => s.token);
  const {
    pendingFiles,
    uploading,
    uploadError,
    setUploadError,
    fileInputRef,
    imageInputRef,
    handleFileSelect,
    removeFile,
    prepareAttachments,
    clearAttachments,
  } = useChatAttachments(token);

  const logEmissionEnabled = useChatStore((s) => s.logEmissionEnabled);

  const pageContext = useChatStore((s) => s.pageContext);
  const effectivePageContext = pageContextOverride ?? pageContext;
  const formContext = useChatStore((s) => s.formContext);

  const setIncludeScreenContext = useChatStore(
    (s) => s.setIncludeScreenContext,
  );
  const setPendingScreenDetection = useChatStore(
    (s) => s.setPendingScreenDetection,
  );

  const setIncludeFormContext = useChatStore((s) => s.setIncludeFormContext);
  const setPendingFormDetection = useChatStore(
    (s) => s.setPendingFormDetection,
  );
  const businessUnits = useBusinessUnitStore((s) => s.businessUnits);
  const setSelectedBusinessUnit = useChatStore(
    (s) => s.setSelectedBusinessUnit,
  );
  const setLogEmissionEnabled = useChatStore((s) => s.setLogEmissionEnabled);
  const { facility } = useEffectiveFacility();

  useEffect(() => {
    if (!screenContextPrompt) return;
    setInput(screenContextPrompt.message);
    setPendingScreenDetection(!!effectivePageContext?.screenContext);
    setPendingFormDetection(
      !!formContext && detectFormReference(screenContextPrompt.message),
    );
    onScreenContextPromptHandled?.();
  }, [
    screenContextPrompt,
    effectivePageContext?.screenContext,
    formContext,
    onScreenContextPromptHandled,
    setPendingScreenDetection,
    setPendingFormDetection,
  ]);

  const handleTranscript = useCallback((text: string) => {
    setInput((prev) => prev + text);
  }, []);

  const { isListening, isSupported, toggleListening } =
    useSpeechRecognition(handleTranscript);

  useEffect(() => {
    if (!showPlaceholderHint || input) return;
    const interval = setInterval(() => {
      setHintIndex((prev) => (prev + 1) % PLACEHOLDER_HINTS.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [showPlaceholderHint, input]);

  const handleSend = useCallback(async () => {
    const hasMessage = input.trim().length > 0;
    const hasFiles = pendingFiles.length > 0;
    if ((!hasMessage && !hasFiles) || disabled || uploading) return;
    const uploaded = await prepareAttachments(() => {
      setPendingScreenDetection(false);
      setPendingFormDetection(false);
    });
    if (uploaded === null) return;
    if (uploaded.length > 0 || hasMessage) {
      const accepted = onSend(
        input.trim(),
        uploaded.length > 0 ? uploaded : undefined,
      );
      if (accepted === false) return;
      setInput("");
      clearAttachments();

      setPendingScreenDetection(false);
      setIncludeScreenContext(null);
      setPendingFormDetection(false);
      setIncludeFormContext(null);
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
    }
  }, [
    input,
    pendingFiles,
    disabled,
    uploading,
    onSend,
    prepareAttachments,
    clearAttachments,
    setPendingScreenDetection,
    setIncludeScreenContext,
    setPendingFormDetection,
    setIncludeFormContext,
  ]);

  const selectSlashItem = useCallback(
    (item: { id: string; label: string; type: "bu" | "action" }) => {
      if (item.type === "bu") {
        const bu = businessUnits.find((b) => String(b.id) === String(item.id));
        if (bu) setSelectedBusinessUnit(bu, contextKey);
      } else if (item.id === "logemission") {
        setLogEmissionEnabled(true);
      }
      setShowSlashMenu(false);
      setInput("");
    },
    [businessUnits, contextKey, setSelectedBusinessUnit, setLogEmissionEnabled],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        if (showSlashMenu) {
          e.preventDefault();
          const item = slashMenuItems[selectedSlashIndex];
          if (item) selectSlashItem(item);
          return;
        }
        e.preventDefault();
        handleSend();
      }
      if (showSlashMenu) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setSelectedSlashIndex((prev) => (prev + 1) % slashMenuItems.length);
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          setSelectedSlashIndex(
            (prev) =>
              (prev - 1 + slashMenuItems.length) % slashMenuItems.length,
          );
        } else if (e.key === "Escape") {
          e.preventDefault();
          setShowSlashMenu(false);
        }
      }
    },
    [
      showSlashMenu,
      slashMenuItems,
      selectedSlashIndex,
      selectSlashItem,
      handleSend,
    ],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      setInput(value);

      const textarea = e.target;
      textarea.style.height = "auto";
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;

      if (value.startsWith("/")) {
        const filterText = value.slice(1);
        if (facility) {
          const items = [
            {
              id: "logemission",
              label: "Log Emission",
              type: "action" as const,
            },
          ];
          setSlashMenuItems(
            filterText
              ? items.filter((item) =>
                  item.label.toLowerCase().includes(filterText),
                )
              : items,
          );
        } else {
          const items = businessUnits.map((bu) => ({
            id: bu.id,
            label: bu.name,
            type: "bu" as const,
          }));
          setSlashMenuItems(
            filterText
              ? items.filter((item) =>
                  item.label.toLowerCase().includes(filterText),
                )
              : items,
          );
        }
        setSelectedSlashIndex(0);
        setShowSlashMenu(true);
      } else if (showSlashMenu) {
        setShowSlashMenu(false);
      }

      if (screenDetectTimer.current) {
        clearTimeout(screenDetectTimer.current);
      }
      screenDetectTimer.current = setTimeout(() => {
        const detected =
          !!effectivePageContext?.screenContext &&
          detectScreenReference(value, effectivePageContext.screenContext);
        setPendingScreenDetection(detected);
        setPendingFormDetection(!!formContext && detectFormReference(value));
      }, 150);
    },
    [
      facility,
      businessUnits,
      showSlashMenu,
      setPendingScreenDetection,
      setPendingFormDetection,
      formContext,
      effectivePageContext?.screenContext,
    ],
  );

  useEffect(() => {
    const handleClickOutside = (e: PointerEvent) => {
      if (
        slashMenuRef.current &&
        !slashMenuRef.current.contains(e.target as Node)
      ) {
        setShowSlashMenu(false);
      }
    };
    if (showSlashMenu) {
      document.addEventListener("pointerdown", handleClickOutside);
    }
    return () =>
      document.removeEventListener("pointerdown", handleClickOutside);
  }, [showSlashMenu]);

  const isSendDisabled =
    disabled ||
    uploading ||
    (!input.trim() && pendingFiles.length === 0) ||
    pendingFiles.some((f) => f.uploading);
  const hasBadges = facility || logEmissionEnabled;

  return (
    <div
      className={cn(
        "w-full shrink-0 border-t border-border bg-card/50 relative min-w-0",
        compact ? "pt-2 pb-2" : "pt-3 pb-3",
      )}
    >
      <div
        className={cn(
          "w-full mx-auto relative",
          compact ? "px-3" : "px-4 md:px-0 max-w-[810px]",
        )}
      >
        <ChatFollowups questions={followUpQuestions} onSend={onSendFollowup} />
        {effectivePageContext?.screenContext && (
          <ContextSharingControls
            hasFormContext={!!formContext}
            pageContext={effectivePageContext}
            getPageContext={getPageContext}
          />
        )}

        <ChatContextConsent
          pageContext={effectivePageContext}
          onSend={handleSend}
        />
        {hasBadges && (
          <div
            className="flex flex-wrap gap-2 mb-2"
            role="list"
            aria-label="Active filters"
          >
            {facility && (
              <Chip
                size="small"
                role="listitem"
                label={facility.name}
                onDelete={() => {
                  setSelectedBusinessUnit(null);
                  setLogEmissionEnabled(false);
                }}
                deleteIcon={
                  <IconButton
                    type="button"
                    size="small"
                    aria-label={`Remove ${facility.name} filter`}
                    sx={{
                      width: 18,
                      height: 18,
                      minWidth: 18,
                      borderRadius: "9999px",
                      "& svg": { color: "inherit" },
                    }}
                  >
                    <X className="h-3 w-3" />
                  </IconButton>
                }
                sx={{
                  gap: "0.25rem",
                  paddingRight: "0.25rem",
                  "& .MuiChip-label": { paddingRight: "0.25rem" },
                }}
              />
            )}
            {logEmissionEnabled && (
              <Chip
                size="small"
                role="listitem"
                label="Log Emission"
                onDelete={() => setLogEmissionEnabled(false)}
                deleteIcon={
                  <IconButton
                    type="submit"
                    size="small"
                    aria-label="Remove Log Emission filter"
                    sx={{
                      width: 18,
                      height: 18,
                      minWidth: 18,
                      borderRadius: "9999px",
                      "& svg": { color: "inherit" },
                    }}
                  >
                    <X className="h-3 w-3" />
                  </IconButton>
                }
                sx={{
                  gap: "0.25rem",
                  paddingRight: "0.25rem",
                  backgroundColor: "var(--primary-10)",
                  color: "var(--primary)",
                  "& .MuiChip-label": { paddingRight: "0.25rem" },
                }}
              />
            )}
          </div>
        )}
        {uploadError && (
          <div
            role="alert"
            className="mb-2 flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          >
            <AlertCircle
              className="mt-0.5 h-3.5 w-3.5 shrink-0"
              aria-hidden="true"
            />
            <span className="flex-1">{uploadError}</span>
            <IconButton
              type="button"
              size="small"
              onClick={() => setUploadError(null)}
              aria-label="Dismiss upload error"
              className="shrink-0"
            >
              <X className="h-3.5 w-3.5" />
            </IconButton>
          </div>
        )}

        <ChatPendingFiles files={pendingFiles} onRemove={removeFile} />
        <div className="border border-border rounded-xl bg-card relative focus-within:ring-1 focus-within:ring-ring/50 pt-1.5 transition-[color,background-color,border-color,box-shadow,opacity,transform,translate,scale,rotate] duration-200">
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.xlsx,.csv,.docx,.txt"
            multiple
            className="hidden"
            onChange={handleFileSelect}
          />
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleFileSelect}
          />
          <TextField
            multiline
            inputRef={textareaRef}
            value={input}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={
              facility
                ? `Ask about ${facility.name}...`
                : showPlaceholderHint
                  ? PLACEHOLDER_HINTS[hintIndex]
                  : "Type / to select, or ask your question..."
            }
            disabled={disabled || uploading}
            aria-label="Chat message input"
            fullWidth
            slotProps={{
              input: {
                sx: {
                  backgroundColor: "transparent",
                  padding: 0,
                  fontSize: "1rem",
                  fontFamily: "inherit",
                  /* The outlined notch renders its own fieldset, which paints a
                     second dark shade over the `bg-card` container. Drop the
                     border and background so the textarea and the icon row
                     below it read as one surface. */
                  "& fieldset": {
                    border: "none",
                    backgroundColor: "transparent",
                  },
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "&.Mui-focused": { boxShadow: "none" },
                },
              },
            }}
            sx={{
              backgroundColor: "transparent",
              "& .MuiInputBase-input": {
                backgroundColor: "transparent",
                padding: { xs: "0.375rem 0.75rem", sm: "0.375rem 1rem" },
                minHeight: "36px",
                color: "var(--foreground)",
                "&::placeholder": {
                  color: "var(--muted-foreground)",
                  opacity: 1,
                },
              },
            }}
          />
          {showSlashMenu && (
            <div
              ref={slashMenuRef}
              className="absolute bottom-full left-3 sm:left-4 mb-1 w-72 max-w-[calc(100vw-2rem)] bg-card border border-border rounded-xl shadow-elevated z-50"
              role="listbox"
              aria-label={facility ? "Actions" : "Business Units"}
              aria-activedescendant={
                slashMenuItems[selectedSlashIndex]
                  ? `slash-${slashMenuItems[selectedSlashIndex].id}`
                  : undefined
              }
            >
              <div className="px-3 py-2 text-xs text-muted-foreground border-b border-border font-medium sticky top-0 bg-card rounded-t-xl">
                {facility ? "Actions" : "Business Units"}
              </div>
              {slashMenuItems.length === 0 ? (
                <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                  No matching items
                </div>
              ) : (
                <div className="max-h-[220px] overflow-y-auto">
                  {slashMenuItems.map((item, idx) => (
                    <Button
                      variant="text"
                      type="button"
                      id={`slash-${item.id}`}
                      key={item.id}
                      role="option"
                      aria-selected={idx === selectedSlashIndex}
                      onClick={() => selectSlashItem(item)}
                      onMouseEnter={() => setSelectedSlashIndex(idx)}
                      className="w-full min-h-[44px] cursor-pointer flex items-center gap-2"
                      sx={{
                        justifyContent: "flex-start",
                        textAlign: "left",
                        padding: "0.625rem 0.75rem",
                        fontSize: "0.875rem",
                        ...(idx === selectedSlashIndex
                          ? {
                              backgroundColor: "var(--primary-10)",
                              color: "var(--primary)",
                              "&:hover": {
                                backgroundColor: "var(--primary-10)",
                              },
                            }
                          : {
                              color: "var(--foreground)",
                              "&:hover": {
                                backgroundColor: "var(--accent)",
                              },
                            }),
                      }}
                    >
                      <span className="truncate">{item.label}</span>
                    </Button>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="flex items-center justify-between px-2 sm:px-3 pb-1.5">
            <IconButton
              size="small"
              disabled={disabled || uploading || pendingFiles.length >= 3}
              aria-label="Attach file"
              aria-haspopup="menu"
              aria-expanded={Boolean(attachAnchor)}
              onClick={(e) => setAttachAnchor(e.currentTarget)}
              sx={{ color: "var(--muted-foreground)" }}
            >
              <Paperclip className="h-4 w-4" />
            </IconButton>
            <Menu
              open={Boolean(attachAnchor)}
              anchorEl={attachAnchor}
              onClose={() => setAttachAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              slotProps={{ paper: { sx: { borderRadius: "0.75rem" } } }}
            >
              <MenuItem
                onClick={() => {
                  setAttachAnchor(null);
                  imageInputRef.current?.click();
                }}
                className="cursor-pointer"
              >
                <Image className="mr-2 h-4 w-4" />
                Image
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setAttachAnchor(null);
                  fileInputRef.current?.click();
                }}
                className="cursor-pointer"
              >
                <FileText className="mr-2 h-4 w-4" />
                Document
              </MenuItem>
            </Menu>
            <div className="flex items-center gap-1.5">
              {isSupported && (
                <IconButton
                  size="small"
                  onClick={toggleListening}
                  disabled={disabled || uploading}
                  aria-label={
                    isListening ? "Stop recording" : "Start voice input"
                  }
                  className={cn(isListening && "animate-pulse")}
                  sx={{
                    color: "var(--muted-foreground)",
                    ...(isListening && {
                      color: "var(--destructive)",
                      backgroundColor: "var(--destructive-10)",
                      border: "1px solid var(--destructive)",
                    }),
                  }}
                >
                  <Mic className="h-4 w-4" />
                </IconButton>
              )}
              {disabled && onStop && (
                <IconButton
                  size="small"
                  onClick={onStop}
                  aria-label="Stop generating"
                  sx={{ color: "var(--destructive)" }}
                >
                  <Square className="h-3.5 w-3.5 fill-current" />
                </IconButton>
              )}
              <IconButton
                size="small"
                onClick={handleSend}
                disabled={isSendDisabled}
                aria-label="Send message"
                sx={{
                  backgroundColor: "var(--primary)",
                  color: "var(--primary-foreground)",
                  "&:hover": {
                    backgroundColor: "var(--primary-90-fg)",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "var(--muted)",
                    color: "var(--muted-foreground)",
                  },
                  "& svg": { color: "inherit" },
                }}
              >
                {uploading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </IconButton>
            </div>
          </div>
        </div>
        {!compact && (
          <div className="text-center text-[11px] text-muted-foreground pt-1.5">
            AI can generate inaccurate data. Verify important information.
          </div>
        )}
      </div>
    </div>
  );
});
